import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectorRef, HostBinding, Renderer2, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseNavigationService, FuseVerticalNavigationComponent, FuseNavigationItem } from '@fuse/components/navigation';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { EventNavigationService } from 'app/core/navigation/event-navigation.service';
import { UserService } from 'app/core/user/user.service';
import {Org} from "@portal/api/responses/organizations/org-details-response";
import {OrganizationSelectorService} from "@portal/shared/services/organization-selector.service";
import {PartySelectedService} from "@portal/shared/services/party-selected.service";
import {PartySettingsService} from "@portal/shared/services/party-settings.service";
import { AppContextService } from '@portal/shared/services/app-context.service';
import { UserPermissionsService } from '@portal/shared/services/user-permissions.service';
import { OrganizationYearContextService } from '@portal/shared/services/organization-year-context.service';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    styleUrls: ['./classy.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    @HostBinding('class.event-navigation-opened') get eventNavOpenedClass() {
        return this.isEventNavigationOpened;
    }

    isScreenSmall: boolean;
    navigation$ = this._navigationService.navigation$;
    user$ = this._userService.user$;
    party$ = this._partySelectedService.currentParty;
    orgList$ = this._organizationSelectorService.orgList$;
    filteredOrgList$: Observable<Org[]> = this.orgList$.pipe(
        map(orgList => this.filterOrgListByContext(orgList))
    );
    eventNavigation: FuseNavigationItem[] = [];
    currentEventName: string | null = null;
    isEventNavigationOpened: boolean = false;
    mainNavigationAppearance: 'default' | 'compact' | 'thin' = 'default';
    selectedOrg: Org | null = this._organizationSelectorService.org;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _organizationSelectorService: OrganizationSelectorService,
        private _navigationService: NavigationService,
        private _eventNavigationService: EventNavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _partySelectedService: PartySelectedService,
        private _partySettingsService: PartySettingsService,
        private _userPermissionsService: UserPermissionsService,
        private _organizationYearContextService: OrganizationYearContextService,
        private _appContextService: AppContextService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _renderer: Renderer2,
        @Inject(DOCUMENT) private _document: Document
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    get isTrial() {
        return this._partySettingsService.isTrial;
    }

    get daysToExpire() {
        return this._partySettingsService.daysToExpire;
    }

    get showPartyTrialBanner(): boolean {
        return !this.isClubContext && this.isTrial;
    }

    get showClubUpgradeBanner(): boolean {
        return this.isClubContext
            && !!this.selectedOrgInContext
            && this._userPermissionsService.organizationPlanRequiresUpgrade;
    }

    get clubPlanDaysToExpire(): number | null {
        return this._userPermissionsService.organizationPlanDaysToExpire;
    }

    get clubPlanIsTrial(): boolean {
        return this._userPermissionsService.organizationPlanIsTrial;
    }

    get clubPlanIsReadOnly(): boolean {
        return this._userPermissionsService.organizationPlanIsReadOnly;
    }

    get clubUpgradeCtaLabel(): string {
        return this.clubPlanIsReadOnly ? 'Regularizar pagamento' : 'Fazer upgrade';
    }

    get availableOrganizationYears(): number[] {
        return this._organizationYearContextService.availableYears;
    }

    get selectedOrganizationYear(): number | null {
        return this._organizationYearContextService.selectedYear;
    }

    get showClubYearSelector(): boolean {
        return this.isClubContext
            && !!this.selectedOrgInContext
            && this.availableOrganizationYears.length > 0;
    }

    get showLayoutHeader(): boolean {
        return this.isScreenSmall || this.showPartyTrialBanner || this.showClubUpgradeBanner || this.showClubYearSelector;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------
    ngOnInit(): void {
        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Subscribe to event navigation data
        this._eventNavigationService.eventNavigationItems$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((items: FuseNavigationItem[]) => {
                this.eventNavigation = items || [];
                if (items && items.length > 0) {
                    this.currentEventName = this._eventNavigationService.getCurrentEventName();
                    // Check if event navigation is opened and update main navigation appearance immediately
                    this.updateMainNavigationAppearance();
                    // Start monitoring if not already started
                    setTimeout(() => {
                        this.startMonitoringEventNav();
                    }, 50);
                } else {
                    this.currentEventName = null;
                    this.isEventNavigationOpened = false;
                    this.mainNavigationAppearance = 'default';
                    this._changeDetectorRef.markForCheck();
                }
            });

        this._organizationSelectorService.orgState$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((org: Org | null) => {
                this.selectedOrg = org;
                this._changeDetectorRef.markForCheck();
            });

        this._organizationYearContextService.availableYears$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._changeDetectorRef.markForCheck();
            });

        this._organizationYearContextService.selectedYear$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._changeDetectorRef.markForCheck();
            });

    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    selectThisOrg(item: Org) {
        if (!this._appContextService.isOrgTypeAllowed(item.type)) {
            return;
        }

        this._organizationSelectorService.setSelectedOrg(item);
        this._partySelectedService.resetParty();
        this._router.navigateByUrl(this._appContextService.defaultRoute());
    }

    get selectedOrgInContext(): Org | null {
        if (!this.selectedOrg || !this._appContextService.isOrgTypeAllowed(this.selectedOrg.type)) {
            return null;
        }

        return this.selectedOrg;
    }

    get isClubContext(): boolean {
        try {
            return this._appContextService.getContext() === 'club';
        } catch {
            return false;
        }
    }

    private filterOrgListByContext(orgList: Org[] | null): Org[] {
        return (orgList ?? []).filter(org => this._appContextService.isOrgTypeAllowed(org.type));
    }

    selectOrganizationYear(year: number): void {
        this._organizationYearContextService.setSelectedYear(year);
    }


    private _eventNavMonitorInterval: any;

    /**
     * Start monitoring event navigation state
     */
    private startMonitoringEventNav(): void {
        // Clear existing interval if any
        if (this._eventNavMonitorInterval) {
            clearInterval(this._eventNavMonitorInterval);
        }

        // Monitor event navigation opened state - check more frequently for faster response
        this._eventNavMonitorInterval = setInterval(() => {
            if (this.eventNavigation && this.eventNavigation.length > 0) {
                const eventNav = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('eventNavigation');
                if (eventNav) {
                    const wasOpened = this.isEventNavigationOpened;
                    this.isEventNavigationOpened = eventNav.opened;
                    if (wasOpened !== eventNav.opened) {
                        this.mainNavigationAppearance = eventNav.opened ? 'thin' : 'default';
                        // Add/remove class on body
                        if (eventNav.opened) {
                            this._renderer.addClass(this._document.body, 'event-navigation-opened');
                        } else {
                            this._renderer.removeClass(this._document.body, 'event-navigation-opened');
                        }
                        this._changeDetectorRef.markForCheck();
                    }
                }
            } else {
                this.isEventNavigationOpened = false;
                this.mainNavigationAppearance = 'default';
                this._renderer.removeClass(this._document.body, 'event-navigation-opened');
                this._changeDetectorRef.markForCheck();
                if (this._eventNavMonitorInterval) {
                    clearInterval(this._eventNavMonitorInterval);
                    this._eventNavMonitorInterval = null;
                }
            }
        }, 50);
    }

    /**
     * Update main navigation appearance based on event navigation state
     */
    updateMainNavigationAppearance(): void {
        const eventNav = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('eventNavigation');
        if (eventNav && this.eventNavigation && this.eventNavigation.length > 0) {
            this.isEventNavigationOpened = eventNav.opened;
            // Change to thin (icon-only) when event navigation is opened
            this.mainNavigationAppearance = eventNav.opened ? 'thin' : 'default';

            // Add/remove class on body
            if (eventNav.opened) {
                this._renderer.addClass(this._document.body, 'event-navigation-opened');
            } else {
                this._renderer.removeClass(this._document.body, 'event-navigation-opened');
            }
        } else {
            this.isEventNavigationOpened = false;
            this.mainNavigationAppearance = 'default';
            this._renderer.removeClass(this._document.body, 'event-navigation-opened');
        }
        this._changeDetectorRef.markForCheck();
    }

    ngOnDestroy(): void {
        // Clear monitoring interval
        if (this._eventNavMonitorInterval) {
            clearInterval(this._eventNavMonitorInterval);
        }

        // Remove class from body
        this._renderer.removeClass(this._document.body, 'event-navigation-opened');

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
